import { collection, getDocs, query, where, getCountFromServer } from "firebase/firestore";
import { db } from "./firebase";

// ════════════════════════════════════════════════════════════════
// Global metrics for the Super-Admin dashboard
//
// These are snapshot reads (not live listeners) to keep Firestore
// costs manageable. User can refresh on demand.
//
// Uses `getCountFromServer` where possible because it returns just
// the count without reading the full documents (much cheaper).
// ════════════════════════════════════════════════════════════════

// Compute start-of-current-month ISO timestamp
function monthStartIso() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString();
}

// Count documents in a collection cheaply
async function countCollection(path) {
  try {
    const snap = await getCountFromServer(collection(db, path));
    return snap.data().count;
  } catch (e) {
    console.warn(`count(${path}) failed:`, e.code || e.message);
    return 0;
  }
}

// Count orders in a school created this month
async function countOrdersThisMonth(schoolId) {
  try {
    const q = query(
      collection(db, "schools", schoolId, "orders"),
      where("timestamp", ">=", monthStartIso())
    );
    const snap = await getCountFromServer(q);
    return snap.data().count;
  } catch (e) {
    console.warn(`countOrdersThisMonth(${schoolId}) failed:`, e.code || e.message);
    return 0;
  }
}

// Count paid invoices this month.
// We intentionally query by createdAt only (not status + createdAt) to avoid
// needing a composite index. Status filtering is done client-side on the
// small result set (invoices from the current month).
async function countPaidInvoicesThisMonth(schoolId) {
  try {
    const q = query(
      collection(db, "schools", schoolId, "invoices"),
      where("createdAt", ">=", monthStartIso())
    );
    const snap = await getDocs(q);
    return snap.docs.filter(d => d.data().status === "pagado").length;
  } catch (e) {
    console.warn(`countPaidInvoicesThisMonth(${schoolId}) failed:`, e.code || e.message);
    return 0;
  }
}

// ────────────────────────────────────────────────────────────────
// loadGlobalMetrics — main entry point
//
// Fetches all schools, then aggregates counters across subcollections.
// Returns a structured object. Costs roughly:
//   O(1) read for schools list
//   O(N) count reads for each of: vendors, parents, students, orders, ordersThisMonth, paidInvoicesThisMonth
// where N = number of schools. So ~6N aggregate-count reads total.
// Aggregate counts are 1/1000 the cost of full reads, so this stays cheap.
// ────────────────────────────────────────────────────────────────
export async function loadGlobalMetrics() {
  const schoolsSnap = await getDocs(collection(db, "schools"));
  const schools = schoolsSnap.docs
    .map(d => ({ ...d.data(), id: d.id }))
    .filter(s => !s.deleted); // exclude soft-deleted

  const activeSchools = schools.filter(s => s.active !== false);

  // Plan distribution
  const planBuckets = { free: 0, basic: 0, premium: 0 };
  for (const s of activeSchools) {
    const plan = s.plan || "free";
    if (planBuckets[plan] !== undefined) planBuckets[plan]++;
  }

  // Per-school aggregates (run in parallel for speed)
  const perSchool = await Promise.all(activeSchools.map(async (s) => {
    const [vendors, parents, students, orders, ordersThisMonth, paidThisMonth] = await Promise.all([
      countCollection(`schools/${s.id}/vendors`),
      countCollection(`schools/${s.id}/parents`),
      countCollection(`schools/${s.id}/students`),
      countCollection(`schools/${s.id}/orders`),
      countOrdersThisMonth(s.id),
      countPaidInvoicesThisMonth(s.id),
    ]);
    return { id: s.id, name: s.name, slug: s.slug, plan: s.plan || "free",
             vendors, parents, students, orders, ordersThisMonth, paidThisMonth };
  }));

  // Global totals
  const totals = perSchool.reduce((acc, s) => {
    acc.vendors += s.vendors;
    acc.parents += s.parents;
    acc.students += s.students;
    acc.orders += s.orders;
    acc.ordersThisMonth += s.ordersThisMonth;
    acc.paidThisMonth += s.paidThisMonth;
    return acc;
  }, { vendors: 0, parents: 0, students: 0, orders: 0, ordersThisMonth: 0, paidThisMonth: 0 });

  // Top 5 schools by total orders
  const topByOrders = [...perSchool]
    .sort((a, b) => b.orders - a.orders)
    .slice(0, 5);

  return {
    schoolsTotal: activeSchools.length,
    schoolsActive: activeSchools.filter(s => s.active !== false).length,
    schoolsSuspended: schools.filter(s => s.active === false && !s.deleted).length,
    planBuckets,
    totals,
    topByOrders,
    loadedAt: new Date().toISOString(),
  };
}
